import {
    BandKind, ObjKind, HAlign, VAlign, bandKindOf, newBand, newDoc, newObj, newPage,
} from './fr3Doc';
import { d2i, isWhitespace, javaTrim, parseJavaDouble, parseJavaLong } from './javaCompat';

/**
 * Parses a FastReport 4 .fr3 (XML) into the Fr3Doc model (port of Fr3Parser).
 *
 * An <inherited ParentReport="tl_portrait.fr3"> template carries only its
 * overrides: nested <inherited Name="X"> elements patch the attributes of the
 * parent component with that name, every other element is a component the
 * child adds. merge() does what FastReport does at load time, with the parent
 * supplied by a ParentProvider: (name) => Uint8Array | null, synchronous - the
 * caller fetches the parents beforehand (see collectParentNames()).
 */
export class Fr3UnsupportedException extends Error {
    constructor(message) {
        super(message);
        this.name = 'Fr3UnsupportedException';
    }
}

/** A parent may itself inherit; FastReport allows it, the corpus never goes deeper than one. */
const MAX_INHERITANCE_DEPTH = 4;

export class Fr3Parser {
    /**
     * @param parentProvider (name) => Uint8Array|null, may throw
     * @param domParserFactory () => DOMParser (defaults to the global one)
     */
    constructor(parentProvider = null, domParserFactory = null) {
        this.parentProvider = parentProvider;
        this.domParserFactory = domParserFactory || (() => new DOMParser());
    }

    /** @param xml Uint8Array (the raw file) or string */
    parse(xml) {
        const doc = newDoc();
        const root = this.resolveInheritance(this.parseDom(xml), doc, 0);

        doc.version = attr(root, 'Version');
        doc.scriptText = attr(root, 'ScriptText.Text');
        doc.printCopies = d2i(num(attr(root, 'PrintOptions.Copies'), 1));
        readVariables(root, doc);

        for (const pageEl of children(root, 'TfrxReportPage')) {
            doc.pages.push(parsePage(pageEl, doc));
        }
        if (doc.pages.length === 0) {
            if (doc.unmergedParent) {
                throw new Fr3UnsupportedException('template inherits from \'' + doc.parentReport
                    + '\' and the parent could not be loaded');
            }
            throw new Fr3UnsupportedException('template has no report page');
        }
        if (doc.pages.length > 1) {
            doc.warnings.push('template has ' + doc.pages.length + ' report pages; only the first is rendered');
        }
        return doc;
    }

    parseDom(xml) {
        let root;
        if (typeof xml === 'string') {
            root = this.parseText(xml);
        } else {
            const declared = declaredEncoding(xml);
            let text = null;
            if (declared === 'utf-8' || declared === 'utf8' || declared === '') {
                text = decodeUtf8Strict(xml);
            } else if (declared === 'windows-1252' || declared === 'cp1252') {
                text = decodeCp1252(xml);
            } else if (declared === 'iso-8859-1' || declared === 'latin1' || declared === 'iso8859-1') {
                text = decodeLatin1(xml);
            } else {
                text = decodeUtf8Strict(xml);
            }
            root = text == null ? null : this.parseText(text);
            if (root == null) {
                // Some templates declare utf-8 but mix in single Windows-1252
                // bytes (an "é" typed by an older designer). FastReport reads
                // them anyway, so do the same: valid UTF-8 stays UTF-8, every
                // stray byte is read as cp1252.
                root = this.parseText(decodeLenient(xml));
                if (root == null) throw new Fr3UnsupportedException('template is not well-formed XML');
            }
        }
        if (root == null) throw new Fr3UnsupportedException('template is not well-formed XML');
        return root;
    }

    /** The document element, or null on a parse error. */
    parseText(text) {
        // The prolog's encoding no longer applies to a decoded string.
        const body = text.replace(/^﻿/, '');
        const dom = this.domParserFactory().parseFromString(body, 'application/xml');
        if (!dom || dom.getElementsByTagName('parsererror').length > 0) return null;
        const root = dom.documentElement;
        if (!root || root.tagName === 'parsererror' || root.tagName === 'html') return null;
        return root;
    }

    // -- inheritance -----------------------------------------------------------

    /** The report element to read: the template itself, or its parent with the overrides applied. */
    resolveInheritance(root, doc, depth) {
        if (root.tagName !== 'inherited') return root;
        const parentName = javaTrim(attr(root, 'ParentReport'));
        doc.parentReport = parentName;
        if (parentName === '') throw new Fr3UnsupportedException('inherited template without ParentReport');
        if (depth >= MAX_INHERITANCE_DEPTH) {
            throw new Fr3UnsupportedException('inheritance deeper than ' + MAX_INHERITANCE_DEPTH + ' levels');
        }
        let parentXml = null;
        if (this.parentProvider) {
            try {
                parentXml = this.parentProvider(parentName);
            } catch (e) {
                parentXml = null;
            }
        }
        if (parentXml == null || parentXml.length === 0) {
            doc.unmergedParent = true;
            throw new Fr3UnsupportedException('template inherits from \'' + parentName
                + '\'; parent template not available');
        }
        const parentRoot = this.resolveInheritance(this.parseDom(parentXml), doc, depth + 1);
        doc.parentReport = parentName;
        merge(parentRoot, root, doc);
        return parentRoot;
    }
}

/**
 * Applies an <inherited> element onto its parent counterpart: attributes
 * override, nested inherited elements recurse by Name, new components are
 * appended, and Datasets/Variables replace the parent's.
 */
export const merge = (target, overlay, doc) => {
    const attrs = overlay.attributes;
    for (let i = 0; i < attrs.length; i++) {
        const a = attrs[i];
        if (a.name === 'ParentReport') continue;
        target.setAttribute(a.name, a.value);
    }
    const owner = target.ownerDocument;
    for (const c of elementChildren(overlay)) {
        const tag = c.tagName;
        if (tag === 'inherited') {
            const name = attr(c, 'Name');
            const match = findByName(target, name);
            if (match == null) {
                doc.warnings.push('inherited component ' + name + ' not found in the parent template');
                continue;
            }
            merge(match, c, doc);
        } else if (tag === 'Datasets' || tag === 'Variables') {
            let existing = null;
            for (const t of elementChildren(target)) {
                if (t.tagName === tag) {
                    existing = t;
                    break;
                }
            }
            const imported = owner.importNode(c, true);
            if (existing != null) target.replaceChild(imported, existing);
            else target.appendChild(imported);
        } else {
            target.appendChild(owner.importNode(c, true));
        }
    }
};

/** Direct children first, then the whole subtree: names are unique per report. */
const findByName = (scope, name) => {
    if (name == null || name === '') return null;
    for (const c of elementChildren(scope)) {
        if (name === (c.getAttribute('Name') || '')) return c;
    }
    for (const c of elementChildren(scope)) {
        const deep = findByName(c, name);
        if (deep != null) return deep;
    }
    return null;
};

const readVariables = (root, doc) => {
    for (const vars of children(root, 'Variables')) {
        for (const item of elementChildren(vars)) {
            const name = javaTrim(attr(item, 'Name'));
            // A leading-space name is a variable category, not a variable.
            if (name === '' || attr(item, 'Name').startsWith(' ')) continue;
            if (!item.hasAttribute('Value')) continue;
            doc.variables.set(name, attr(item, 'Value'));
        }
    }
    // FastReport 4 stores the report's Variables (RepTitle, RepSelection,
    // RepLicenceName, VarBetm1...) in the root's PropData stream, not as
    // XML; the XML list above wins when both exist.
    const propData = attr(root, 'PropData');
    if (propData !== '') {
        for (const item of propDataListItems(decodeHex(propData), 'Variables')) {
            const rawName = xmlAttr(item, 'Name');
            if (rawName == null) continue;
            const name = javaTrim(rawName);
            if (name === '' || rawName.startsWith(' ')) continue;
            const value = xmlAttr(item, 'Value');
            if (value == null) continue;
            if (!doc.variables.has(name)) doc.variables.set(name, value);
        }
    }
};

// -- root PropData (Delphi DefineProperties stream) -------------------------------

const VA_NULL = 0, VA_LIST = 1, VA_INT8 = 2, VA_INT16 = 3, VA_INT32 = 4, VA_EXTENDED = 5,
    VA_STRING = 6, VA_IDENT = 7, VA_FALSE = 8, VA_TRUE = 9, VA_BINARY = 10, VA_SET = 11,
    VA_LSTRING = 12, VA_NIL = 13, VA_COLLECTION = 14, VA_SINGLE = 15, VA_CURRENCY = 16,
    VA_DATE = 17, VA_WSTRING = 18, VA_INT64 = 19, VA_UTF8STRING = 20;

class PropDataError extends Error {}

const byteAt = (d, i) => {
    if (i < 0 || i >= d.length) throw new PropDataError('out of range');
    return d[i];
};

const int32 = (d, i) => (byteAt(d, i) | (byteAt(d, i + 1) << 8) | (byteAt(d, i + 2) << 16) | (byteAt(d, i + 3) << 24));

const sliceChecked = (d, from, len) => {
    if (len < 0 || from + len > d.length) throw new PropDataError('out of range');
    return d.subarray(from, from + len);
};

/** A string value of the given type at pos (type byte already read), else null (pos untouched). */
const readStringValue = (d, pos, type) => {
    let len;
    switch (type) {
        case VA_STRING:
        case VA_IDENT:
            len = byteAt(d, pos.i);
            pos.i += 1;
            return readCp1252(d, pos, len);
        case VA_LSTRING:
            len = int32(d, pos.i);
            pos.i += 4;
            return readCp1252(d, pos, len);
        case VA_UTF8STRING: {
            len = int32(d, pos.i);
            pos.i += 4;
            const s = decodeUtf8Java(sliceChecked(d, pos.i, len));
            pos.i += len;
            return s;
        }
        case VA_WSTRING: {
            len = int32(d, pos.i);
            pos.i += 4;
            const bytes = sliceChecked(d, pos.i, len * 2);
            let s = '';
            for (let k = 0; k < len; k++) s += String.fromCharCode(bytes[2 * k] | (bytes[2 * k + 1] << 8));
            pos.i += len * 2;
            return s;
        }
        default:
            return null;
    }
};

const readCp1252 = (d, pos, len) => {
    const s = decodeCp1252(sliceChecked(d, pos.i, len));
    pos.i += len;
    return s;
};

/** java new String(bytes, UTF_8): malformed sequences become U+FFFD. */
const decodeUtf8Java = (bytes) => {
    try {
        return new TextDecoder('utf-8').decode(bytes);
    } catch (e) {
        return decodeLatin1(bytes);
    }
};

const skipValue = (d, pos) => {
    const type = byteAt(d, pos.i++);
    switch (type) {
        case VA_NULL: case VA_FALSE: case VA_TRUE: case VA_NIL:
            return;
        case VA_LIST:
            while (byteAt(d, pos.i) !== VA_NULL) skipValue(d, pos);
            pos.i++;
            return;
        case VA_INT8: pos.i += 1; return;
        case VA_INT16: pos.i += 2; return;
        case VA_INT32: case VA_SINGLE: pos.i += 4; return;
        case VA_EXTENDED: pos.i += 10; return;
        case VA_CURRENCY: case VA_DATE: case VA_INT64: pos.i += 8; return;
        case VA_BINARY: {
            const len = int32(d, pos.i);
            pos.i += 4 + len;
            return;
        }
        case VA_SET:
            for (;;) {
                const n = byteAt(d, pos.i++);
                if (n === 0) return;
                pos.i += n;
            }
        case VA_COLLECTION:
            while (byteAt(d, pos.i) !== VA_NULL) {
                const t = byteAt(d, pos.i);
                if (t === VA_INT8 || t === VA_INT16 || t === VA_INT32) skipValue(d, pos);
                // an item: vaList of properties
                if (byteAt(d, pos.i) === VA_LIST) {
                    pos.i++;
                    for (;;) {
                        const nameLen = byteAt(d, pos.i++);
                        if (nameLen === 0) break;
                        pos.i += nameLen;
                        skipValue(d, pos);
                    }
                }
            }
            pos.i++;
            return;
        default:
            if (readStringValue(d, pos, type) == null) {
                throw new PropDataError('unknown PropData value type ' + type);
            }
    }
};

const collectStrings = (d, pos, out) => {
    const type = byteAt(d, pos.i++);
    if (type === VA_LIST) {
        while (byteAt(d, pos.i) !== VA_NULL) collectStrings(d, pos, out);
        pos.i++;
        return;
    }
    const s = readStringValue(d, pos, type);
    if (s != null) {
        out.push(s);
    } else {
        pos.i--;
        skipValue(d, pos);
    }
};

/**
 * The item strings of one list property in a TReader/TWriter property
 * stream, e.g. Variables: vaList, then per item vaList + vaString
 * ' Name="RepTitle" Value="'...'"' + vaNull, then vaNull. Empty on any
 * format surprise - a missing variable is better than a refused report.
 */
export const propDataListItems = (d, property) => {
    const out = [];
    if (d == null) return out;
    const pos = { i: 0 };
    try {
        while (pos.i < d.length) {
            const nameLen = byteAt(d, pos.i++);
            if (nameLen === 0) break;
            const name = decodeLatin1(sliceChecked(d, pos.i, nameLen));
            pos.i += nameLen;
            if (name === property) {
                collectStrings(d, pos, out);
                return out;
            }
            skipValue(d, pos);
        }
    } catch (e) {
        if (!(e instanceof PropDataError)) throw e;
        out.length = 0;
    }
    return out;
};

/**
 * One attribute of a FastReport item string (' Name="x" Value="y"'), with
 * frxXMLToStr unescaping (&quot; &amp; &lt; &gt; &#N;); null when absent.
 */
export const xmlAttr = (item, attribute) => {
    const key = attribute + '="';
    let at = -1;
    let from = 0;
    for (;;) {
        const i = item.indexOf(key, from);
        if (i < 0) break;
        if (i === 0 || isWhitespace(item.charAt(i - 1))) {
            at = i;
            break;
        }
        from = i + 1;
    }
    if (at < 0) return null;
    const start = at + key.length;
    let end = item.indexOf('"', start);
    if (end < 0) end = item.length;
    return unescapeXml(item.substring(start, end));
};

const unescapeXml = (s) => {
    if (s.indexOf('&') < 0) return s;
    let sb = '';
    let i = 0;
    while (i < s.length) {
        const c = s.charAt(i);
        if (c === '&') {
            const semi = s.indexOf(';', i);
            if (semi > i) {
                const ent = s.substring(i + 1, semi);
                let rep = null;
                if (ent === 'quot') rep = '"';
                else if (ent === 'amp') rep = '&';
                else if (ent === 'lt') rep = '<';
                else if (ent === 'gt') rep = '>';
                else if (ent === 'apos') rep = "'";
                else if (ent.startsWith('#')) {
                    // Java Integer.parseInt: optional sign, int32 range, (char) = low 16 bits.
                    const hex = ent.startsWith('#x') || ent.startsWith('#X');
                    const digits = hex ? ent.substring(2) : ent.substring(1);
                    if ((hex ? /^[+-]?[0-9A-Fa-f]+$/ : /^[+-]?[0-9]+$/).test(digits)) {
                        const code = parseInt(digits, hex ? 16 : 10);
                        if (code >= -2147483648 && code <= 2147483647) rep = String.fromCharCode(code & 0xFFFF);
                    }
                }
                if (rep != null) {
                    sb += rep;
                    i = semi + 1;
                    continue;
                }
            }
        }
        sb += c;
        i++;
    }
    return sb;
};

// -- structure -------------------------------------------------------------------

const parsePage = (el, doc) => {
    const p = newPage();
    p.name = attr(el, 'Name');
    p.paperWidthMm = num(attr(el, 'PaperWidth'), 210);
    p.paperHeightMm = num(attr(el, 'PaperHeight'), 297);
    p.leftMarginMm = num(attr(el, 'LeftMargin'), 0);
    p.rightMarginMm = num(attr(el, 'RightMargin'), 0);
    p.topMarginMm = num(attr(el, 'TopMargin'), 0);
    p.bottomMarginMm = num(attr(el, 'BottomMargin'), 0);

    for (const bandEl of elementChildren(el)) {
        const tag = bandEl.tagName;
        const kind = bandKindOf(tag);
        if (kind === BandKind.UNKNOWN) {
            if (tag.startsWith('Tfrx')) {
                doc.warnings.push('page element ' + tag + ' ' + attr(bandEl, 'Name') + ' not rendered');
            }
            continue;
        }
        p.bands.push(parseBand(bandEl, kind, doc));
    }
    // The XML lists bands in arbitrary order; the designer's vertical order is
    // what defines the report structure. (Stable sort, like Collections.sort.)
    p.bands.sort((a, b) => compareDouble(a.top, b.top));
    return p;
};

const compareDouble = (a, b) => (a < b ? -1 : (a > b ? 1 : 0));

const parseBand = (el, kind, doc) => {
    const band = newBand();
    band.kind = kind;
    band.rawTag = el.tagName;
    band.name = attr(el, 'Name');
    band.left = num(attr(el, 'Left'), 0);
    band.top = num(attr(el, 'Top'), 0);
    band.width = num(attr(el, 'Width'), 0);
    band.height = num(attr(el, 'Height'), 0);
    band.condition = attr(el, 'Condition');
    band.onBeforePrint = attr(el, 'OnBeforePrint');
    band.dataSetName = attr(el, 'DataSetName');
    band.childName = attr(el, 'Child');
    band.stretched = bool(attr(el, 'Stretched'), false);
    band.visible = bool(attr(el, 'Visible'), true);
    band.reprintOnNewPage = bool(attr(el, 'ReprintOnNewPage'), false);
    band.startNewPage = bool(attr(el, 'StartNewPage'), false);

    for (const objEl of elementChildren(el)) {
        const o = parseObj(objEl, doc);
        if (o != null) band.objects.push(o);
    }
    return band;
};

const parseObj = (el, doc) => {
    let kind;
    switch (el.tagName) {
        case 'TfrxMemoView':
        case 'TfrxSysMemoView': kind = ObjKind.MEMO; break;
        case 'TfrxPictureView': kind = ObjKind.PICTURE; break;
        case 'TfrxBarCodeView': kind = ObjKind.BARCODE; break;
        case 'TfrxLineView': kind = ObjKind.LINE; break;
        case 'TfrxShapeView': kind = ObjKind.SHAPE; break;
        default:
            if (el.tagName.startsWith('Tfrx')) {
                doc.warnings.push('object ' + el.tagName + ' ' + attr(el, 'Name') + ' not rendered');
            }
            return null;
    }
    const o = newObj();
    o.kind = kind;
    o.name = attr(el, 'Name');
    o.left = num(attr(el, 'Left'), 0);
    o.top = num(attr(el, 'Top'), 0);
    o.width = num(attr(el, 'Width'), 0);
    o.height = num(attr(el, 'Height'), 0);

    o.text = attr(el, 'Text');
    o.dataField = attr(el, 'DataField');
    o.dataSetName = attr(el, 'DataSetName');

    const fontName = attr(el, 'Font.Name');
    if (fontName !== '') o.fontName = fontName;
    o.fontHeight = num(attr(el, 'Font.Height'), -13);
    o.fontStyle = d2i(num(attr(el, 'Font.Style'), 0));
    o.fontColor = color(attr(el, 'Font.Color'), 0x000000);

    o.frameTyp = d2i(num(attr(el, 'Frame.Typ'), 0));
    o.frameColor = color(attr(el, 'Frame.Color'), 0x000000);
    o.frameWidth = num(attr(el, 'Frame.Width'), 1);

    // FastReport 4 writes the background as Color; 2023+ writes
    // Fill.BackColor (with FillType="ftBrush"). Templates use both.
    let colorAttr = attr(el, 'Color');
    if (colorAttr === '') colorAttr = attr(el, 'Fill.BackColor');
    // Still absent means clNone: no fill at all, not white.
    o.color = colorAttr === '' ? null : color(colorAttr, 0xFFFFFF);
    // clNone written explicitly.
    if (javaTrim(colorAttr) === '536870911') o.color = null;

    o.hAlign = hAlign(attr(el, 'HAlign'));
    o.vAlign = vAlign(attr(el, 'VAlign'));

    o.formatStr = attr(el, 'DisplayFormat.FormatStr');
    o.formatKind = attr(el, 'DisplayFormat.Kind');
    o.decimalSeparator = attr(el, 'DisplayFormat.DecimalSeparator');
    o.hideZeros = bool(attr(el, 'HideZeros'), false);
    o.visible = bool(attr(el, 'Visible'), true);
    o.wordWrap = bool(attr(el, 'WordWrap'), true);
    o.stretchMode = attr(el, 'StretchMode');
    o.allowHtmlTags = bool(attr(el, 'AllowHTMLTags'), false);

    o.center = bool(attr(el, 'Center'), false);
    o.transparent = bool(attr(el, 'Transparent'), false);

    const propData = attr(el, 'Picture.PropData');
    if (propData !== '') o.embeddedImage = stripGraphicStream(decodeHex(propData));

    o.barType = attr(el, 'BarType');
    o.expression = attr(el, 'Expression');
    o.showText = bool(attr(el, 'ShowText'), true);
    o.calcCheckSum = bool(attr(el, 'CalcCheckSum'), true);
    o.rotation = d2i(num(attr(el, 'Rotation'), 0));
    o.zoom = num(attr(el, 'Zoom'), 1);

    o.diagonal = bool(attr(el, 'Diagonal'), false);
    o.shape = attr(el, 'Shape');
    if (kind === ObjKind.SHAPE && o.shape === '') o.shape = 'skRectangle';

    return o;
};

// -- embedded pictures -------------------------------------------------------------

/**
 * Picture.PropData is a Delphi DefineProperties stream, not an image: "04
 * 'Data' 0A len" then the graphic's class name and, for most classes, a
 * 4-byte size before the file itself. Returns the bare file (BMP/PNG/JPEG/GIF)
 * or null when no known image signature is found.
 */
export const stripGraphicStream = (data) => {
    if (data == null || data.length < 4) return null;
    if (imageAt(data, 0) > 0) return data;
    const limit = Math.min(data.length - 2, 96);
    for (let i = 1; i < limit; i++) {
        const len = imageAt(data, i);
        if (len <= 0) continue;
        const end = Math.min(data.length, i + len);
        return data.slice(i, end);
    }
    return null;
};

/** Length of the image file starting at i, or 0. A BMP carries its own size; the others run to the end. */
const imageAt = (d, i) => {
    const remaining = d.length - i;
    if (remaining < 4) return 0;
    const b0 = d[i];
    const b1 = d[i + 1];
    const b2 = d[i + 2];
    const b3 = d[i + 3];
    if (b0 === 0x42 && b1 === 0x4D && remaining >= 14) {
        const size = d[i + 2] + d[i + 3] * 0x100 + d[i + 4] * 0x10000 + d[i + 5] * 0x1000000;
        // "BM" also occurs by chance; a real header's size fits the stream.
        if (size >= 26 && size <= remaining) return size;
        return 0;
    }
    if (b0 === 0x89 && b1 === 0x50 && b2 === 0x4E && b3 === 0x47) return remaining;
    if (b0 === 0xFF && b1 === 0xD8 && b2 === 0xFF) return remaining;
    if (b0 === 0x47 && b1 === 0x49 && b2 === 0x46 && b3 === 0x38) return remaining;
    return 0;
};

// -- attribute helpers ---------------------------------------------------------------

const attr = (el, name) => {
    const v = el.getAttribute(name);
    return v == null ? '' : v;
};

/** fr3 writes decimals with the authoring machine's separator, normally a comma. */
export const num = (raw, fallback) => {
    if (raw == null) return fallback;
    let s = javaTrim(raw);
    if (s === '') return fallback;
    s = s.split(',').join('.');
    const v = parseJavaDouble(s);
    return v == null ? fallback : v;
};

const bool = (raw, fallback) => {
    if (raw == null || javaTrim(raw) === '') return fallback;
    return javaTrim(raw).toLowerCase() === 'true';
};

const hAlign = (raw) => {
    switch (raw == null ? '' : javaTrim(raw)) {
        case 'haRight': return HAlign.RIGHT;
        case 'haCenter': return HAlign.CENTER;
        case 'haBlock': return HAlign.BLOCK;
        default: return HAlign.LEFT;
    }
};

const vAlign = (raw) => {
    switch (raw == null ? '' : javaTrim(raw)) {
        case 'vaBottom': return VAlign.BOTTOM;
        case 'vaCenter': return VAlign.CENTER;
        default: return VAlign.TOP;
    }
};

/**
 * Delphi TColor: positive values are 0x00BBGGRR, negative values are system
 * colours (0x80000000 | index). Returns 0xRRGGBB.
 */
export const color = (raw, fallback) => {
    if (raw == null || javaTrim(raw) === '') return fallback;
    const v = parseJavaLong(javaTrim(raw));
    if (v == null) return fallback;
    if (v < 0) return systemColor(((v % 256) + 256) % 256, fallback);
    const bgr = v % 0x1000000;
    const r = bgr & 0xFF;
    const g = (bgr >> 8) & 0xFF;
    const b = (bgr >> 16) & 0xFF;
    return (r << 16) | (g << 8) | b;
};

const systemColor = (index, fallback) => {
    switch (index) {
        case 5: return 0xFFFFFF; // clWindow
        case 7: return 0x000000; // clMenuText
        case 8: return 0x000000; // clWindowText
        case 14: return 0xFFFFFF; // clHighlightText
        case 15: return 0xF0F0F0; // clBtnFace
        case 16: return 0xA0A0A0; // clBtnShadow
        case 18: return 0x000000; // clBtnText
        default: return fallback;
    }
};

const decodeHex = (s) => {
    const t = s.replace(/[^0-9A-Fa-f]/g, '');
    if (t.length < 2) return null;
    const n = Math.trunc(t.length / 2);
    const out = new Uint8Array(n);
    for (let i = 0; i < n; i++) out[i] = parseInt(t.substring(i * 2, i * 2 + 2), 16);
    return out;
};

const elementChildren = (parent) => {
    const out = [];
    const nl = parent.childNodes;
    for (let i = 0; i < nl.length; i++) {
        if (nl[i].nodeType === 1) out.push(nl[i]);
    }
    return out;
};

const children = (parent, tag) => elementChildren(parent).filter((e) => e.tagName === tag);

// -- byte decoding (what the Java XML parser does with the raw file) ------------

/** The prolog's encoding, lower case, or '' when none is declared. */
export const declaredEncoding = (bytes) => {
    let head = '';
    const n = Math.min(bytes.length, 200);
    for (let i = 0; i < n; i++) head += String.fromCharCode(bytes[i]);
    const m = /^(?:﻿|ï»¿)?<\?xml[^>]*encoding\s*=\s*["']([^"']+)["']/i.exec(head);
    return m ? m[1].trim().toLowerCase() : '';
};

const CP1252_HIGH = [
    0x20AC, 0xFFFD, 0x201A, 0x0192, 0x201E, 0x2026, 0x2020, 0x2021, 0x02C6, 0x2030, 0x0160, 0x2039, 0x0152, 0xFFFD, 0x017D, 0xFFFD,
    0xFFFD, 0x2018, 0x2019, 0x201C, 0x201D, 0x2022, 0x2013, 0x2014, 0x02DC, 0x2122, 0x0161, 0x203A, 0x0153, 0xFFFD, 0x017E, 0x0178,
];

const cp1252Char = (b) => String.fromCharCode(b >= 0x80 && b <= 0x9F ? CP1252_HIGH[b - 0x80] : b);

export const decodeCp1252 = (bytes) => {
    let s = '';
    for (let i = 0; i < bytes.length; i++) s += cp1252Char(bytes[i]);
    return s;
};

export const decodeLatin1 = (bytes) => {
    let s = '';
    for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
    return s;
};

/** Length of the valid UTF-8 sequence at i, or 0 when it is malformed. */
const utf8SeqLen = (b, i) => {
    const c = b[i];
    const len = c < 0x80 ? 1 : (c >= 0xC2 && c <= 0xDF) ? 2 : (c >= 0xE0 && c <= 0xEF) ? 3
        : (c >= 0xF0 && c <= 0xF4) ? 4 : 0;
    if (len === 0 || i + len > b.length) return 0;
    for (let k = 1; k < len; k++) {
        if ((b[i + k] & 0xC0) !== 0x80) return 0;
    }
    return len;
};

/** Code point of a structurally valid sequence, or -1 when overlong / a surrogate / out of range. */
const utf8CodePoint = (b, i, len) => {
    let cp;
    if (len === 1) return b[i];
    if (len === 2) return ((b[i] & 0x1F) << 6) | (b[i + 1] & 0x3F);
    if (len === 3) {
        cp = ((b[i] & 0x0F) << 12) | ((b[i + 1] & 0x3F) << 6) | (b[i + 2] & 0x3F);
        if (cp < 0x800 || (cp >= 0xD800 && cp <= 0xDFFF)) return -1;
        return cp;
    }
    cp = ((b[i] & 0x07) << 18) | ((b[i + 1] & 0x3F) << 12) | ((b[i + 2] & 0x3F) << 6) | (b[i + 3] & 0x3F);
    if (cp < 0x10000 || cp > 0x10FFFF) return -1;
    return cp;
};

/** Strict UTF-8 (a BOM is dropped); null on any malformed sequence. */
export const decodeUtf8Strict = (b) => {
    // The browser's fatal decoder applies the same rules (no overlongs, no
    // surrogates) and is far faster on templates with large embedded logos.
    if (typeof TextDecoder !== 'undefined') {
        try {
            return new TextDecoder('utf-8', { fatal: true }).decode(b);
        } catch (e) {
            return null;
        }
    }
    let i = (b.length >= 3 && b[0] === 0xEF && b[1] === 0xBB && b[2] === 0xBF) ? 3 : 0;
    const parts = [];
    let chunk = '';
    while (i < b.length) {
        const len = utf8SeqLen(b, i);
        if (len === 0) return null;
        const cp = utf8CodePoint(b, i, len);
        if (cp < 0) return null;
        chunk += String.fromCodePoint(cp);
        if (chunk.length > 8192) {
            parts.push(chunk);
            chunk = '';
        }
        i += len;
    }
    parts.push(chunk);
    return parts.join('');
};

/** UTF-8 where the bytes form valid sequences, Windows-1252 for every byte that does not. */
export const decodeLenient = (b) => {
    const parts = [];
    let chunk = '';
    let i = 0;
    while (i < b.length) {
        let len = utf8SeqLen(b, i);
        if (len === 1) {
            chunk += String.fromCharCode(b[i]);
        } else if (len > 1) {
            const cp = utf8CodePoint(b, i, len);
            // Java's String(bytes, "UTF-8") replaces an overlong / surrogate form.
            chunk += cp < 0 ? '�'.repeat(len) : String.fromCodePoint(cp);
        } else {
            chunk += cp1252Char(b[i]);
            len = 1;
        }
        if (chunk.length > 8192) {
            parts.push(chunk);
            chunk = '';
        }
        i += len;
    }
    parts.push(chunk);
    return parts.join('');
};

/**
 * Parent template names an inherited template needs, from its raw text,
 * so the caller can fetch them (asynchronously) before parsing.
 */
export const parentReportName = (bytesOrText) => {
    const text = typeof bytesOrText === 'string' ? bytesOrText : decodeLenient(bytesOrText.slice(0, 4096));
    const m = /<inherited\b[^>]*\bParentReport\s*=\s*"([^"]*)"/.exec(text);
    return m ? javaTrim(m[1]) : '';
};

export default Fr3Parser;
